import { useState } from "react";
import ReactECharts from "echarts-for-react";
import type { CategoryMergeProcessingQueryEngine, CategoryStatItem } from "@/types/jobMarket";

interface CategoryChartProps {
  data: CategoryStatItem[];
  compare: boolean;
  /** Все вакансии периода — знаменатель доли категории и global_pct в разборе */
  totalVacancies?: number;
  mergeProcessingQueryEngine?: CategoryMergeProcessingQueryEngine;
}

export const CATEGORY_ROW_H = 70;

const CATEGORY_COLORS: Record<string, string> = {
  language: "#0abf53",
  processing: "#10b981",
  streaming: "#06b6d4",
  orchestration: "#8b5cf6",
  transformation: "#f59e0b",
  database: "#3b82f6",
  cloud_dw: "#6366f1",
  cloud: "#f97316",
  infra: "#ef4444",
  data_lake: "#14b8a6",
  bi: "#ec4899",
};

const SHOWN_CATEGORIES = [
  "language",
  "database",
  "orchestration",
  "processing",
  "data_lake",
  "cloud",
  "infra",
  "bi",
];

const CATEGORY_DISPLAY_LABELS: Record<string, string> = {
  processing: "Вычисления",
};

function mergeQueryEngineIntoProcessing(
  data: CategoryStatItem[],
  totalVacancies: number | undefined,
  mergePe?: CategoryMergeProcessingQueryEngine
): CategoryStatItem[] {
  const processing = data.find((d) => d.category === "processing");
  const queryEngine = data.find((d) => d.category === "query_engine");

  if (!processing || !queryEngine) return data;

  const vacDen =
    totalVacancies && totalVacancies > 0
      ? totalVacancies
      : data.reduce((s, d) => s + d.current_count, 0);

  const mergedBreakdownRaw = [...processing.breakdown, ...queryEngine.breakdown];
  const mergedSum = mergedBreakdownRaw.reduce((s, t) => s + t.current_count, 0);
  const mergedBreakdown = mergedBreakdownRaw
    .map((t) => ({
      ...t,
      current_pct:
        mergedSum > 0 ? Math.round((t.current_count / mergedSum) * 1000) / 10 : 0,
      global_pct:
        vacDen > 0 ? Math.round((t.current_count / vacDen) * 1000) / 10 : 0,
    }))
    .sort((a, b) => b.current_pct - a.current_pct);

  /** DISTINCT vacancy по обеим категориям — с бэкенда; иначе сумма завысит пересечения */
  const mergedCount =
    mergePe?.vacancy_count ?? processing.current_count + queryEngine.current_count;
  const mergedPct = vacDen > 0 ? Math.round((mergedCount / vacDen) * 1000) / 10 : 0;

  const mergedTopTechs = [
    ...new Set([...processing.top_techs, ...queryEngine.top_techs]),
  ].slice(0, 4);

  const merged: CategoryStatItem = {
    ...processing,
    label: "Вычисления",
    current_pct: mergedPct,
    current_count: mergedCount,
    breakdown: mergedBreakdown,
    top_techs: mergedTopTechs,
  };

  return data
    .filter((d) => d.category !== "query_engine")
    .map((d) => (d.category === "processing" ? merged : d));
}

export function CategoryChart({
  data,
  compare,
  totalVacancies,
  mergeProcessingQueryEngine,
}: CategoryChartProps) {
  const [expanded, setExpanded] = useState<string | null>(null);

  if (!data.length) return null;

  const merged = mergeQueryEngineIntoProcessing(
    data,
    totalVacancies,
    mergeProcessingQueryEngine
  );
  const filtered = merged.filter((d) => SHOWN_CATEGORIES.includes(d.category));
  const sorted = [...filtered].sort((a, b) => b.current_count - a.current_count);

  return (
    <div className="space-y-2">
      {sorted.map((cat) => (
        <CategoryRow
          key={cat.category}
          item={{ ...cat, label: CATEGORY_DISPLAY_LABELS[cat.category] ?? cat.label }}
          compare={compare}
          expanded={expanded === cat.category}
          onToggle={() => setExpanded(expanded === cat.category ? null : cat.category)}
          totalVacancies={totalVacancies}
        />
      ))}
    </div>
  );
}

function categoryShareOfVacancies(
  item: CategoryStatItem,
  totalVacancies: number | undefined
): number {
  if (totalVacancies != null && totalVacancies > 0) {
    return Math.round((item.current_count / totalVacancies) * 1000) / 10;
  }
  return item.current_pct;
}

function CategoryRow({
  item,
  compare,
  expanded,
  onToggle,
  totalVacancies,
}: {
  item: CategoryStatItem;
  compare: boolean;
  expanded: boolean;
  onToggle: () => void;
  totalVacancies?: number;
}) {
  const color = CATEGORY_COLORS[item.category] ?? "#0abf53";
  const delta = item.delta;
  const pctGlobal = categoryShareOfVacancies(item, totalVacancies);

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors text-left"
      >
        <div className="w-52 flex-shrink-0">
          <div className="font-medium text-gray-900 text-sm">{item.label}</div>
          <div className="text-xs text-gray-400 mt-0.5 truncate">
            {item.top_techs.slice(0, 3).join(" · ")}
          </div>
        </div>

        <div className="flex-1 flex items-center gap-3">
          <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden relative">
            {compare && item.previous_pct !== undefined && (
              <div
                className="absolute inset-y-0 left-0 rounded-full opacity-40"
                style={{ width: `${item.previous_pct}%`, background: color }}
              />
            )}
            <div
              className="absolute inset-y-0 left-0 rounded-full"
              style={{ width: `${Math.min(100, pctGlobal)}%`, background: color }}
            />
          </div>
          <div className="w-12 text-right font-semibold text-gray-800 text-sm">
            {pctGlobal}%
          </div>
        </div>

        {compare && delta !== undefined && (
          <div
            className={`w-16 text-right text-sm font-semibold ${
              delta > 0 ? "text-emerald-600" : delta < 0 ? "text-red-500" : "text-gray-400"
            }`}
          >
            {delta > 0 ? "▲" : delta < 0 ? "▼" : "—"}{" "}
            {delta !== 0 ? `${Math.abs(delta)}%` : ""}
          </div>
        )}

        <div className={`text-gray-400 transition-transform ml-2 ${expanded ? "rotate-180" : ""}`}>
          ▾
        </div>
      </button>

      {expanded && item.breakdown.length > 0 && (
        <div className="border-t border-gray-100 px-5 py-4 bg-gray-50">
          <BreakdownChart data={item.breakdown} color={color} />
        </div>
      )}
    </div>
  );
}

function BreakdownChart({
  data,
  color,
}: {
  data: { tech: string; current_pct: number; global_pct?: number }[];
  color: string;
}) {
  const sorted = [...data].sort((a, b) => b.current_pct - a.current_pct);
  const option = {
    tooltip: {
      trigger: "axis" as const,
      appendToBody: true,
      confine: false,
      axisPointer: { type: "shadow" as const },
      formatter: (params: { name: string; value: number }[]) => {
        const p = params[0];
        const item = sorted.find((d) => d.tech === p.name);
        let html = `<strong>${p.name}</strong><br/>${p.value}% упоминаний внутри категории`;
        if (item?.global_pct != null) {
          html += `<br/><span style="color:#9ca3af;font-size:11px">${item.global_pct}% от числа вакансий в периоде</span>`;
        }
        return html;
      },
    },
    grid: { left: 140, right: 50, top: 4, bottom: 4, containLabel: false },
    xAxis: {
      type: "value" as const,
      max: 100,
      axisLabel: { formatter: "{value}%", color: "#9ca3af", fontSize: 10 },
      splitLine: { lineStyle: { color: "#f3f4f6" } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    yAxis: {
      type: "category" as const,
      data: sorted.map((d) => d.tech),
      inverse: true,
      axisLabel: { color: "#374151", fontSize: 11, width: 130, overflow: "truncate" as const },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series: [
      {
        type: "bar" as const,
        data: sorted.map((d) => ({
          value: d.current_pct,
          itemStyle: { color, borderRadius: [0, 3, 3, 0], opacity: 0.85 },
        })),
        barMaxWidth: 18,
        label: {
          show: true,
          position: "right" as const,
          formatter: "{c}%",
          color: "#6b7280",
          fontSize: 10,
        },
      },
    ],
  };

  const h = Math.max(120, data.length * 28 + 20);
  return <ReactECharts option={option} style={{ height: h, width: "100%" }} notMerge />;
}
